"use client";

import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { io, Socket } from 'socket.io-client';

interface NotificationContextType {
  unreadCount: number;
  chatNotifications: Map<string, number>;
  markChatAsRead: (chatId: string) => void;
  playNotificationSound: boolean;
  toggleNotificationSound: () => void;
  connectionStatus: 'connecting' | 'connected' | 'disconnected' | 'error';
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const NotificationProvider: React.FC<{
  children: React.ReactNode;
  currentUser?: { id: string } | null;
}> = ({ children, currentUser }) => {
  const [unreadCount, setUnreadCount] = useState(0);
  const [chatNotifications, setChatNotifications] = useState<Map<string, number>>(new Map());
  const [playNotificationSound, setPlayNotificationSound] = useState(true);
  const [connectionStatus, setConnectionStatus] = useState<'connecting' | 'connected' | 'disconnected' | 'error'>('disconnected');
  const socketRef = useRef<Socket>();
  const initialLoadDoneRef = useRef(false);

  useEffect(() => {
    if (!currentUser?.id) {
      setConnectionStatus('disconnected');
      return;
    }
    
    setConnectionStatus('connecting');
    
    // const socketUrl = process.env.NEXT_PUBLIC_SOCKET_URL;
    const socketUrl = "http://localhost:5000/";
    
    const socket = io(socketUrl!, {
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      transports: ['websocket']
    });

    socketRef.current = socket;

    socket.on('connect', () => {
      setConnectionStatus('connected');
      
      socket.emit('register', currentUser.id);
      
      socket.emit('getUnreadNotifications', currentUser.id);
    });

    socket.on('registerConfirmed', (data) => {
      // console.log('Registro confirmado:', data);
    });

    socket.on('connect_error', (error) => {
      console.error('Error de conexión socket:', error);
      setConnectionStatus('error');
    });

    socket.on('disconnect', () => {
      setConnectionStatus('disconnected');
    });

    socket.on('error', (error) => {
      console.error('Error del servidor:', error);
    });

    socket.on('unreadNotifications', (data: { chatNotifications: [string, number][], totalCount: number }) => {
      
      if (!initialLoadDoneRef.current) {
        const notificationMap = new Map<string, number>(data.chatNotifications);
        setChatNotifications(notificationMap);
        setUnreadCount(data.totalCount);
        initialLoadDoneRef.current = true;
      }
    });

    return () => {
      socket.disconnect();
      initialLoadDoneRef.current = false;
      setConnectionStatus('disconnected');
    };
  }, [currentUser?.id]);

  useEffect(() => {
    if (!socketRef.current || !currentUser?.id) return;

    const socket = socketRef.current;

    const handleReceiveMessage = (messageData: {
      receiverId: string;
      chatId: string;
    }) => { 
      if (messageData.receiverId === currentUser.id) {
        setUnreadCount(prev => {
          const newCount = prev + 1;
          // console.log(` Contador de notificaciones actualizado: ${prev} → ${newCount}`);
          return newCount;
        });
        
        setChatNotifications(prev => {
          const newMap = new Map(prev);
          const currentCount = newMap.get(messageData.chatId) || 0;
          newMap.set(messageData.chatId, currentCount + 1);
          // console.log(` Nueva notificación para chat ${messageData.chatId}:`, 
          //   `${currentCount} → ${currentCount + 1}`); 
          return newMap;
        });
      }
    };

    const handleChatRead = ({ chatId }: { chatId: string }) => {
      
      setChatNotifications(prev => {
        const newMap = new Map(prev);
        const count = newMap.get(chatId) || 0;
        
        if (count > 0) {
          newMap.delete(chatId);
          
          setUnreadCount(prevCount => {
            const newCount = Math.max(0, prevCount - count);
            return newCount;
          });
        // } else {
        //   console.log(`ℹ️ No hay notificaciones para eliminar en chat ${chatId}`);
        }
        
        return newMap;
      });
    };

    socket.on('receiveMessage', handleReceiveMessage);
    socket.on('chatRead', handleChatRead);

    return () => {
      socket.off('receiveMessage', handleReceiveMessage);
      socket.off('chatRead', handleChatRead);
    };
  }, [currentUser?.id]);

  const markChatAsRead = (chatId: string) => {
    // console.log(` Marcando chat como leído: ${chatId}`);
    
    if (!socketRef.current) {
      console.warn('Socket no disponible, no se puede marcar chat como leído');
      return;
    }

    socketRef.current.emit('markChatAsRead', chatId);

    setChatNotifications(prev => {
      const newMap = new Map(prev);
      const count = newMap.get(chatId) || 0;
      
      if (count > 0) {
        // console.log(` Eliminando ${count} notificaciones locales para chat ${chatId}`);
        newMap.delete(chatId);
        
        setUnreadCount(prevCount => {
          const newCount = Math.max(0, prevCount - count);
          // console.log(` Contador local actualizado: ${prevCount} → ${newCount}`);
          return newCount;
        });
      // } else {
      //   console.log(`ℹ️ No hay notificaciones locales para eliminar en chat ${chatId}`);
      }
      
      return newMap;
    });
  };

  const toggleNotificationSound = () => {
    setPlayNotificationSound(prev => !prev);
  };

  const contextValue = {
    unreadCount,
    chatNotifications,
    markChatAsRead,
    playNotificationSound,
    toggleNotificationSound,
    connectionStatus
  };

  return (
    <NotificationContext.Provider value={contextValue}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};